// File: src/components/layout/theme-toggle.tsx
// Purpose: Theme toggle button for dark/light/system modes for InstaDev
// InstaDev - Theme Toggle Component


import React, { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { useThemeStore } from '@/lib/stores/theme-store'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sun, Moon, Monitor, Check } from 'lucide-react'

interface ThemeToggleProps {
  className?: string
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, setTheme, effectiveTheme, setEffectiveTheme } = useThemeStore()

  // Update effective theme based on system preference
  useEffect(() => {
    const updateEffectiveTheme = () => {
      if (theme === 'system') {
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        setEffectiveTheme(systemPrefersDark ? 'dark' : 'light')
      } else {
        setEffectiveTheme(theme)
      }
    }

    updateEffectiveTheme()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', updateEffectiveTheme)

    return () => mediaQuery.removeEventListener('change', updateEffectiveTheme)
  }, [theme, setEffectiveTheme])

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement
    
    root.classList.remove('light', 'dark')
    root.classList.add(effectiveTheme)
  }, [effectiveTheme])

  const themes = [
    {
      name: 'light',
      label: 'Light',
      icon: Sun,
      description: 'Light theme',
    },
    {
      name: 'dark',
      label: 'Dark',
      icon: Moon,
      description: 'Dark theme',
    },
    {
      name: 'system',
      label: 'System',
      icon: Monitor,
      description: 'Follow system preference',
    },
  ] as const

  const currentThemeConfig = themes.find(t => t.name === theme) || themes[2]
  const CurrentIcon = currentThemeConfig.icon

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-9 w-9 p-0', className)}
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {themes.map((themeOption) => {
          const IconComponent = themeOption.icon
          const isActive = theme === themeOption.name

          return (
            <DropdownMenuItem
              key={themeOption.name}
              onClick={() => setTheme(themeOption.name)}
              className="flex items-center gap-2"
            >
              <IconComponent className="h-4 w-4" />
              <span className="flex-1">{themeOption.label}</span>
              {isActive && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}